import React, { useEffect } from 'react';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { styled } from '@mui/material/styles';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { DropdownMenu } from 'src/components/BeaconV2Components/ExperimentalLayout/Advertising/components/DropdownMenu';
import NumberFormat from 'react-number-format';
import { InputAdornment, TextField } from '@mui/material';
import RuleStore from './stores';
import colors from 'src/utils/colors';
import { useAppSelector } from 'src/utils/Hooks/typedHooks';

const CustomTextField = styled(TextField)({
  '& .MuiInputBase-root': {
    height: '32px',
    borderRadius: '4px',
    border: `1px solid ${colors.graysSecondary}`,
    backgroundColor: '#fff',
    padding: '5px'
  },
  '& .MuiInputLabel-root': {
    display: 'none'
  },
  'input::-webkit-outer-spin-button,input::-webkit-inner-spin-button': {
    '-webkit-appearance': 'none',
    margin: '0'
  },

  'input[type=number]': {
    '-moz-appearance': 'textfield'
  },

  input: {
    height: '19px',
    fontFamily: 'Roboto',
    fontSize: '14px',
    fontWeight: 'normal',
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: 'normal',
    letterSpacing: 'normal',
    textAlign: 'left',
    color: colors.darkBlue,
    padding: '0px'
  }
});

export const RuleConditions = React.memo(() => {
  const { rules, setRules } = RuleStore((s) => ({
    rules: s.rules,
    setRules: s.setRules
  }));

  const currencySymbol = useAppSelector((state) => state?.retailer?.currencySymbol) || '$';

  useEffect(() => {
    const val = (
      rules.ruleStrategy === 'increase' ? rules.ruleStrategyUpperBound : rules.ruleStrategyLowerBound
    )?.toString();
  }, [rules]);

  return (
    <Flex flexDirection="column">
      <Text variant="h7">Conditions</Text>
      <Flex className="ruleRow">
        <DropdownMenu
          selectedId={rules.ruleStrategy}
          options={[
            { id: 'increase', label: 'Increase' },
            { id: 'decrease', label: 'Decrease' }
          ]}
          onChange={(e) => {
            setRules({ ...rules, ruleStrategy: e.id as 'increase' | 'decrease' });
          }}
        />
        <DropdownMenu
          selectedId={rules.ruleTarget}
          options={[
            { id: 'adCampaign', label: 'Campaign Budgets' },
            { id: 'adTarget', label: 'Target Bids' }
          ]}
          onChange={(e) => {
            setRules({ ...rules, ruleTarget: e.id as 'adCampaign' | 'adTarget' });
          }}
        />
        <DropdownMenu
          options={[
            { id: '%', label: 'by %' },
            { id: '$', label: ` by ${currencySymbol}` }
          ]}
          onChange={(e) => {
            setRules({ ...rules, ruleStrategyMetric: e.id as '%' | '$' });
          }}
          selectedId={rules.ruleStrategyMetric}
        />
        <NumberFormat
          customInput={CustomTextField}
          name="value"
          placeholder={`${rules.ruleStrategyMetric === '$' ? currencySymbol : ''}0.00${
            rules.ruleStrategyMetric === '%' ? '%' : ''
          }`}
          sx={{ width: '66px' }}
          value={rules.value}
          onValueChange={({ floatValue }) => {
            // If floatValue is undefined, default to 0.
            if (floatValue > 100 && rules.ruleStrategyMetric === '%') {
              floatValue = 100.0;
            }
            setRules({ ...rules, value: floatValue });
          }}
          prefix={rules.ruleStrategyMetric === '$' ? currencySymbol : ''}
          suffix={rules.ruleStrategyMetric === '%' ? '%' : ''}
          InputProps={{
            sx: {
              'input::placeholder': {
                color: '#BEC7D3',
                opacity: 1
              }
            }
          }}
          decimalScale={2}
          fixedDecimalScale
          isNumericString
          allowNegative={false}
          required
        />
        {rules.ruleStrategy === 'increase' ? 'up to' : 'down to'}
        <NumberFormat
          customInput={CustomTextField}
          name="ruleStrategyBounds"
          placeholder="0.00"
          value={rules.ruleStrategy === 'increase' ? rules.ruleStrategyUpperBound : rules.ruleStrategyLowerBound}
          sx={{ width: 'auto', minWidth: '66px', maxWidth: '111px' }}
          InputProps={{
            startAdornment: (
              <InputAdornment
                position="start"
                sx={{
                  '& .MuiTypography-root': {
                    color: colors.darkBlue
                  }
                }}
              >
                {currencySymbol}
              </InputAdornment>
            ),
            sx: {
              '& .MuiInputBase-input': {
                width: 'auto',
                fieldSizing: 'content'
              },
              'input::placeholder': {
                color: '#BEC7D3',
                opacity: 1
              }
            }
          }}
          onValueChange={({ floatValue }) => {
            const value = floatValue === undefined ? 0 : floatValue;
            setRules({
              ...rules,
              ...(rules.ruleStrategy === 'increase'
                ? { ruleStrategyUpperBound: value, ruleStrategyLowerBound: undefined }
                : { ruleStrategyLowerBound: value, ruleStrategyUpperBound: undefined })
            });
          }}
          decimalScale={2}
          fixedDecimalScale
          isNumericString
          allowNegative={false}
          required
        />
      </Flex>
    </Flex>
  );
});

RuleConditions.displayName = 'RuleConditions';
